import type { JSX } from "react";
import { useMemo, useState } from "react";
import type { AnalyticsFieldKey, SegmentDefinition } from "@adlywise/shared";
import { categoriesForConfig, type AnalyticsConfig } from "./ConfigurePanel";
import {
  AddRowButton,
  FieldPicker,
  RemoveButton,
  narrowCategories,
} from "./RowBuilder";
import { FieldRow, type Condition } from "./filters/AnalyticsCategoryBody";
import { asArray } from "./filters/analyticsConditionUtils";

// Filters block of the analytics side panel: one row per filtered property,
// Include-only and AND-ed, emitted as a SegmentDefinition { all: [...] } that
// the next run reads. Any property the config can filter by is offered
// (categoriesForConfig), grouped or not: a filter on a property the report does
// not break down by is a plain WHERE.
//
// Rows are shown under their category, in the categories' own order and each
// category's field order, not in the order they were added. A new row starts
// as Category → Property (red until picked) and stays red until it has a
// value; an empty condition is not sent (useAnalyticsAggregate drops it).

type AnalyticsFiltersSectionProps = {
  config: AnalyticsConfig;
  value: SegmentDefinition;
  onChange: (nextValue: SegmentDefinition) => void;
};

const uid = (): string =>
  crypto.randomUUID?.() ??
  `c-${Date.now().toString()}-${Math.random().toString(16).slice(2)}`;

export const AnalyticsFiltersSection = ({
  config,
  value,
  onChange,
}: AnalyticsFiltersSectionProps): JSX.Element => {
  const categories = useMemo(
    () =>
      categoriesForConfig({ source: config.source, counters: config.counters }),
    [config.source, config.counters],
  );
  // null until a draft was first opened; false once one closed, which hands
  // focus back to the "add" line.
  const [isAdding, setIsAdding] = useState<boolean | null>(null);
  const conditions = (value.all ?? []) as Condition[];
  const used = new Set(conditions.map((c) => c.field));
  const available = narrowCategories(categories, (key) => !used.has(key));

  const setConditions = (next: Condition[]): void =>
    onChange({ ...value, all: next as SegmentDefinition["all"] });

  const replace = (next: Condition): void =>
    setConditions(conditions.map((c) => (c.field === next.field ? next : c)));

  const remove = (field: string): void =>
    setConditions(conditions.filter((c) => c.field !== field));

  const add = (field: AnalyticsFieldKey): void => {
    setConditions([...conditions, { id: uid(), field, op: "in", value: [] }]);
    setIsAdding(false);
  };

  const byField = new Map(conditions.map((c) => [c.field, c]));
  const groups = categories
    .map((category) => ({
      category,
      fields: category.fields.filter((key) => byField.has(key)),
    }))
    .filter((group) => group.fields.length > 0);

  return (
    <div className="flex flex-col gap-3">
      {conditions.length === 0 && isAdding !== true && (
        <p className="text-[12px] text-(--text-muted)">
          No filters: every request in the period counts.
        </p>
      )}
      {groups.map(({ category, fields }) => {
        const Icon = category.icon;
        return (
          <div
            key={category.id}
            role="group"
            aria-label={category.label}
            className="flex flex-col gap-1.5"
          >
            <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
              <Icon size={13} strokeWidth={2} />
              {category.label}
            </span>
            {fields.map((key) => {
              const descriptor = category.fieldDescriptors[key];
              const condition = byField.get(key);
              if (!descriptor || !condition) return null;
              const isEmpty = asArray(condition.value).length === 0;
              return (
                <div
                  key={key}
                  aria-invalid={isEmpty || undefined}
                  className={`rounded-(--radius-md) border px-3 py-2 ${
                    isEmpty
                      ? "border-(--status-danger) bg-(--status-danger)/5"
                      : "border-(--border-subtle) bg-(--surface-2)"
                  }`}
                >
                  <FieldRow
                    descriptor={descriptor}
                    condition={condition}
                    onChange={replace}
                    trailing={
                      <RemoveButton
                        name={`${descriptor.label} filter`}
                        onClick={() => remove(key)}
                      />
                    }
                  />
                </div>
              );
            })}
          </div>
        );
      })}
      {isAdding && available.length > 0 ? (
        <FieldPicker
          name="filter"
          categories={available}
          onPick={add}
          onCancel={() => setIsAdding(false)}
        />
      ) : available.length > 0 ? (
        <AddRowButton
          label="Add filter"
          autoFocus={isAdding === false}
          onClick={() => setIsAdding(true)}
        />
      ) : null}
    </div>
  );
};
